import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Check, X, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* =====================================================================
 * EditableValue
 * Inline editable numeric/text value. In private mode shows a small pencil
 * icon on hover; click to edit; ⏎ to save, esc to cancel.
 * ===================================================================== */
export const EditableValue = ({
  value, onSave, type = "number", suffix = "", className = "", inputClassName = "",
  placeholder = "—", testId, disabled = false,
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? "");
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => { setDraft(value ?? ""); }, [value]);
  React.useEffect(() => { if (editing) setTimeout(() => ref.current?.focus(), 30); }, [editing]);

  const commit = async () => {
    if (busy) return;
    const next = type === "number" ? (draft === "" ? null : Number(draft)) : draft;
    if (next === value) { setEditing(false); return; }
    setBusy(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar.");
    } finally { setBusy(false); }
  };

  if (disabled) {
    return (
      <span className={className} data-testid={testId}>
        {value ?? placeholder}{suffix && <span className="text-hx-muted ml-1">{suffix}</span>}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={testId}
        className={`group inline-flex items-baseline gap-1 cursor-pointer hover:bg-hx-neon/[0.04] rounded px-1 -mx-1 transition-colors ${className}`}
        title="Clique para editar"
      >
        <span className={inputClassName}>{value != null && value !== "" ? value : placeholder}</span>
        {suffix && <span className="text-hx-muted text-[12px]">{suffix}</span>}
        <Pencil size={9} strokeWidth={1.8} className="opacity-0 group-hover:opacity-60 text-hx-neon transition-opacity" />
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={ref}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
        }}
        onBlur={commit}
        className={`bg-black/60 border border-hx-neon/40 rounded px-2 py-0.5 text-hx-text outline-none font-mono tabular-nums ${inputClassName}`}
        style={{ width: type === "number" ? 110 : 200, minWidth: 0 }}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {busy && <Loader2 size={11} className="animate-spin text-hx-neon" />}
    </span>
  );
};

/* =====================================================================
 * EditButton — small action icon used on row hover
 * ===================================================================== */
export const RowActionButton = ({ Icon, onClick, danger = false, busy = false, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded transition-colors disabled:opacity-50 ${
      danger ? "text-hx-muted hover:bg-red-500/10 hover:text-red-300" : "text-hx-muted hover:bg-white/[0.03] hover:text-hx-text"
    }`}
  >
    {busy ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} strokeWidth={2} />}
  </button>
);

/* =====================================================================
 * AddItemForm — collapsed "+ Add" button that expands into an inline form.
 * `fields` is an array of { key, label, type, placeholder, required }
 * ===================================================================== */
export const AddItemForm = ({ fields, onSubmit, addLabel = "Adicionar", testId, compact = false }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({});

  const reset = () => setForm({});
  const submit = async (e) => {
    e?.preventDefault();
    const required = fields.filter((f) => f.required);
    for (const f of required) {
      if (!form[f.key]) { toast.error(`${f.label} é obrigatório`); return; }
    }
    setBusy(true);
    try {
      const payload = {};
      for (const f of fields) {
        const raw = form[f.key];
        if (raw === undefined || raw === "" || raw === null) continue;
        payload[f.key] = f.type === "number" ? Number(raw) : raw;
      }
      await onSubmit(payload);
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 ${compact ? "py-1.5" : "py-2"} rounded border border-dashed border-white/15 hover:border-hx-neon/40 text-[10px] uppercase tracking-[0.18em] text-hx-muted hover:text-hx-neon transition-all`}
      >
        <Plus size={11} strokeWidth={2.2} /> {addLabel}
      </button>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      onSubmit={submit}
      className="mt-3 rounded-md border border-white/[0.10] bg-black/40 p-3 space-y-2"
      data-testid={testId ? `${testId}-form` : undefined}
    >
      {fields.map((f) => (
        <label key={f.key} className="flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-[0.2em] text-hx-muted">{f.label}{f.required ? " *" : ""}</span>
          <input
            type={f.type || "text"}
            inputMode={f.type === "number" ? "decimal" : undefined}
            step={f.type === "number" ? "0.01" : undefined}
            placeholder={f.placeholder}
            value={form[f.key] ?? ""}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            className="bg-black/50 border border-white/[0.10] focus:border-hx-neon/40 rounded px-2 py-1.5 text-[12px] text-hx-text outline-none transition-colors font-mono placeholder:text-hx-muted/40"
            autoFocus={f === fields[0]}
            data-testid={testId ? `${testId}-input-${f.key}` : undefined}
          />
        </label>
      ))}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={() => { reset(); setOpen(false); }} className="px-3 py-1 rounded border border-white/[0.10] text-[9px] uppercase tracking-[0.18em] text-hx-muted hover:text-hx-text transition-colors">
          <X size={10} strokeWidth={2} className="inline mr-1" /> Cancelar
        </button>
        <button type="submit" disabled={busy} data-testid={testId ? `${testId}-submit` : undefined} className="px-3 py-1 rounded bg-hx-neon text-hx-bg text-[9px] uppercase tracking-[0.18em] font-semibold hover:bg-hx-neon-soft transition-colors disabled:opacity-50 inline-flex items-center gap-1">
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} strokeWidth={2.4} />} Guardar
        </button>
      </div>
    </motion.form>
  );
};

export { Pencil, Check, X, Plus, Trash2 };
